import React, { useEffect, useState } from 'react';
import { t } from 'ttag';
import { Button } from '~/ui/buttons';
import { useCarouselStoreForSequence } from '../carousel/CarouselStore';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { CAROUSEL_NEXT } from '../itemIds';
import audioEffect from '~/assets/mp3/light_switches.mp3';
import flipEffect from '~/assets/mp3/card_flip.mp3';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { select } from '~/lib/select';
import { useEditorStore } from '~/shell/Store';

const selector = (s: TrainingState) =>
  select(s, 'auditoryFeedback', 'activateSequence');

const cardFlipAudioEffect = new Audio(flipEffect);
const audio = new Audio(audioEffect);
cardFlipAudioEffect.load();
audio.load();

interface SequenceItem {
  id: string | number;
  audios?: Array<{ id: string | number }>;
}

interface Props {
  sequence?: { items: SequenceItem[] };
  carouselId?: string;
}

export type CarouselNextDefinition = ItemDefinition<
  typeof CAROUSEL_NEXT,
  void | Props
>;

export class CarouselNext extends Item<typeof CAROUSEL_NEXT, void | Props> {
  protected render({
    id,
    parent,
    sequence,
    carouselId
  }: RenderProps<void | Props> & {
    sequence?: { items: SequenceItem[] };
    carouselId?: string;
  }) {
    if (!carouselId && !parent?.id) {
      throw new Error(
        'CarouselNext must have a carouselId or parent with an id'
      );
    }
    const sequenceId = carouselId || parent?.id || '';
    const { next, variant } = useCarouselStoreForSequence(sequenceId);
    const { auditoryFeedback } = useTrainingStore(selector);
    const { blockers } = useSequenceStore();
    const { editorMode } = useEditorStore();
    const [isDisabled, setIsDisabled] = useState(false);

    const onClick = () => {
      if (auditoryFeedback && !editorMode) {
        audio.play().catch(() => {
          // Silently ignore autoplay restrictions
        });
        cardFlipAudioEffect.play().catch(() => {
          // Silently ignore autoplay restrictions
        });
      }

      next();
    };

    useEffect(() => {
      if (sequence && blockers.length > 0) {
        const _id = id.split('.')[0];
        const element = sequence.items.find(
          (item: SequenceItem) => String(item.id) === _id
        );

        if (element && element.audios && element.audios.length > 0) {
          const audioElementId = element.audios[0].id;

          if (blockers.includes(audioElementId as string)) {
            setIsDisabled(true);
          } else {
            setIsDisabled(false);
          }
        }
      }
    }, [blockers, id, sequence]);

    if (variant === 'grid' || variant === 'dynamic') return null;

    return <Button disabled={isDisabled} onClick={onClick}>{t`Next`}</Button>;
  }
}

registry[CAROUSEL_NEXT] = CarouselNext;
